import { Container, type ContainerOptions } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import type { Insets, Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

/** Where on the screen a HUD group sits. */
export type HudCorner =
  | "top-left"
  | "top"
  | "top-right"
  | "left"
  | "center"
  | "right"
  | "bottom-left"
  | "bottom"
  | "bottom-right";

/** How the children inside the group are stacked. */
export type HudFlow = "vertical" | "horizontal" | "none";

export interface HudAnchorOptions extends ContainerOptions {
  corner?: HudCorner;
  /** Kept clear of the screen edges — the TV-safe margin. */
  safeArea?: Insets;
  flow?: HudFlow;
  /** Gap between children when flowing. */
  gap?: number;
}

/**
 * Pins a group of HUD controls to a point on the screen and keeps them there
 * when it resizes.
 *
 * `Control.anchor` and `dock` already handle a control's position inside its
 * parent; what a HUD needs on top of that is the *screen* edge — a health bar
 * that stays bottom-left however the window is dragged about, and a safe area
 * so nothing important sits where a TV would crop it or a phone would put a
 * notch.
 *
 * The group sizes itself to its contents, so nothing has to be measured by
 * hand: fill it, and it takes up exactly as much room as it needs in the
 * corner it was given.
 */
export class HudAnchor extends Container {
  corner: HudCorner;
  safeArea: Insets;
  flow: HudFlow;
  gap: number;

  /** Stops `layoutHud` re-entering itself through the sizes it just set. */
  #laying = false;
  /** The desktop this is currently listening to for resizes. */
  #watching: Desktop | null = null;
  /** Children this is listening to for resizes. */
  #watched = new Set<Control>();

  constructor(options: HudAnchorOptions = {}) {
    super({ width: 200, height: 100, ...options });
    this.corner = options.corner ?? "top-left";
    this.safeArea = options.safeArea ?? { left: 16, top: 16, right: 16, bottom: 16 };
    this.flow = options.flow ?? "vertical";
    this.gap = options.gap ?? 6;
  }

  /** The group is a frame round its children; clicks go to them, not to it. */
  protected override hitTestSelf(): boolean {
    return false;
  }

  /**
   * Stack the children, shrink to them, and move into the corner.
   *
   * Driven by events rather than from `onPaint`, and that is not a detail:
   * a parent pushes a child's transform before calling its `paint`, so a
   * control that moves itself while painting spends a frame drawn in its old
   * place. For a HUD pinned to a screen edge that reads as a flash across the
   * screen every time anything resizes.
   */
  layoutHud(): void {
    if (this.#laying) return;
    this.#laying = true;
    try {
      this.#flowChildren();
      this.#shrinkToContents();
      this.#moveToCorner();
    } finally {
      this.#laying = false;
    }
  }

  #flowChildren(): void {
    if (this.flow === "none") return;
    const visible = this.controls.toArray().filter((c) => c.visible);
    let along = 0;
    for (const child of visible) {
      if (this.flow === "vertical") {
        child.setLocation(child.x, along);
        along += child.height + this.gap;
      } else {
        child.setLocation(along, child.y);
        along += child.width + this.gap;
      }
    }
  }

  #shrinkToContents(): void {
    let width = 0;
    let height = 0;
    for (const child of this.controls) {
      if (!child.visible) continue;
      width = Math.max(width, child.x + child.width);
      height = Math.max(height, child.y + child.height);
    }
    if (width !== this.width || height !== this.height) this.setSize(width, height);
  }

  #moveToCorner(): void {
    const parent = this.parent;
    if (!parent) return;
    // The work area rather than the whole desktop, where there is one: a HUD
    // has to keep clear of reserved chrome — a title bar across the top, a
    // taskbar along the bottom — for the same reason a maximized window does.
    // Any other parent has no such notion and hands back its client rect.
    const area: Rect = (parent as Partial<{ workArea: Rect }>).workArea ?? parent.clientRect;
    const safe = this.safeArea;

    const left = area.x + safe.left;
    const right = area.x + area.width - safe.right - this.width;
    const top = area.y + safe.top;
    const bottom = area.y + area.height - safe.bottom - this.height;
    const middleX = area.x + (area.width - this.width) / 2;
    const middleY = area.y + (area.height - this.height) / 2;

    const x =
      this.corner.endsWith("left") || this.corner === "left"
        ? left
        : this.corner.endsWith("right") || this.corner === "right"
          ? right
          : middleX;
    const y = this.corner.startsWith("top")
      ? top
      : this.corner.startsWith("bottom")
        ? bottom
        : middleY;
    this.setLocation(Math.round(x), Math.round(y));
  }

  protected override performLayout(): void {
    this.layoutHud();
  }

  override onChildrenChanged(): void {
    super.onChildrenChanged();
    this.#watchChildren();
    this.layoutHud();
  }

  /**
   * Follow the desktop's size *and* its work area, so the corner stays a
   * corner when the window is resized or a bar appears along an edge.
   * `performLayout` covers this control's own bounds changing; nothing tells
   * a child that its parent grew or that the area it may use shrank.
   */
  protected override onEnvironmentChanged(): void {
    const desktop = this.desktop;
    if (desktop !== this.#watching) {
      this.#watching?.off("resize", this.#onOwnerResized);
      this.#watching?.off("workareachanged", this.#onOwnerResized);
      this.#watching = desktop;
      desktop?.on("resize", this.#onOwnerResized);
      desktop?.on("workareachanged", this.#onOwnerResized);
    }
    this.#watchChildren();
    this.layoutHud();
  }

  #onOwnerResized = (): void => {
    this.layoutHud();
  };

  /**
   * Follow each child's size too: a stat bar that gains a caption or a quest
   * tracker that gains a line changes how much room the group needs, and the
   * corner has to take up the slack.
   */
  #watchChildren(): void {
    for (const child of this.controls) {
      if (this.#watched.has(child)) continue;
      this.#watched.add(child);
      child.on("resize", this.#onOwnerResized);
    }
  }

  /** Add a control and re-flow, which is the usual way to fill one of these. */
  addHud<T extends Control>(control: T): T {
    this.controls.add(control);
    this.layoutHud();
    return control;
  }

  protected override onPaint(_rc: RenderContext, _theme: Theme): void {
    // A frame round its children and nothing more. Any background belongs to
    // a Panel put inside it, so the gaps between HUD pieces stay see-through.
  }
}
